import { TextFieldProps, TextField } from '@nexelem/components';
import { get } from 'lodash';
import { Control, FieldValues, Path, useController, RegisterOptions } from 'react-hook-form';
import { FormInputWrapper, FormInputWrapperProps } from '../FormInputWrapper';
import { FC, useMemo } from 'react';
import { ProLightCircleExclamation } from '@nexelem/icons/ProLightCircleExclamation';
import { IconComponentProps } from '@nexelem/icons';

type InternalFormInputProps<Value extends FieldValues> = {
  readonly name: Path<Value>;
  readonly control?: Control<Value>;
  readonly label?: string;
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly maxWidth?: FormInputWrapperProps['maxWidth'];
  readonly rules?: Omit<
    RegisterOptions<Value, Path<Value>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  readonly HelperTextIcon?: FC<IconComponentProps>;
  readonly showHelperTextIcon?: boolean;
  readonly showErrorMessage?: boolean;
};

type FormInputProps<Value extends FieldValues> = Omit<TextFieldProps<string>, keyof InternalFormInputProps<Value>> &
  InternalFormInputProps<Value>;

export function TextFormInput<Value extends FieldValues>({
  label,
  placeholder,
  maxWidth = 560,
  showErrorMessage = true,
  showHelperTextIcon = true,
  HelperTextIcon = ProLightCircleExclamation,
  helperText,
  disabled,
  ...props
}: FormInputProps<Value>) {
  const { field, formState } = useController(props);
  const { name } = props;

  const errorMessageContent = get(formState.errors, [name, 'message'].join('.')) as unknown as string;

  const errorMessage = useMemo(() => {
    if (!showErrorMessage) {
      return undefined;
    }
    return errorMessageContent;
  }, [errorMessageContent, showErrorMessage]);

  const hasError = !!get(formState.errors, name);

  return (
    <FormInputWrapper fullWidth maxWidth={maxWidth}>
      <TextField
        {...field}
        {...props}
        disabled={disabled}
        errorMessage={errorMessage}
        helperText={helperText}
        label={label}
        placeholder={placeholder}
        showErrorBorder={hasError}
      />
    </FormInputWrapper>
  );
}
